import { inject, NgModule } from "@angular/core";
import { Routes, RouterModule, ROUTES, PreloadAllModules, Router } from "@angular/router";
import { AuthGuard } from "./service/authGuard/auth.guard";
import { CountryGuard } from "./service/countryGuard/country.guard";
import { UserTypeGuard } from "./service/user-type/user-type.guard";
import { NotFoundGuard } from "./service/not-found/not-found.guard";
import { IsTeacherGuard } from "./service/is-teacher.guard";
import { MFAGuard } from "./service/guards/mfa.guard";
import { ConfigService } from "./service/config/config.service";
import { PtEeGuard } from "./service/guards/pt-ee.guard";
import { Store } from "@ngrx/store";
import { ThemeNames } from "./appsettings";

function routesFactory(config: ConfigService = inject(ConfigService)) {
  const routes: Routes = [
    {
      path: "",
      loadComponent: () => import('./pages/init/init.component').then(c => c.InitComponent),
      canActivate: [AuthGuard],
      data: {
        allowedUsers: ["all"],
      },
      pathMatch: "full",
    },
    {
      path: "signin-callback",
      loadComponent: () => import('./pages/signin-callback/signin-callback.component').then(c => c.SiginCallbackComponent),
      data: {
        name: "init",
        allowedUsers: ["all"],
      },
    },
    {
      path: "select-suomi-ward",
      loadComponent: () => import('./pages/select-suomi-ward/select-suomi-ward.component').then(c => c.SelectSuomiWardComponent),
      canActivate: [AuthGuard],
    },
    {
      path: "select-suomi-ward/:any",
      loadComponent: () => import('./pages/select-suomi-ward/select-suomi-ward.component').then(c => c.SelectSuomiWardComponent),
      canActivate: [AuthGuard],
    },
    {
      path: "redirect",
      loadComponent: () => import('./pages/redirect/redirect.component').then(c => c.RedirectComponent),
    },
    {
      path: "renewtoken",
      loadComponent: () => import('./pages/renewtoken/renewtoken.component').then(c => c.RenewtokenComponent),
      data: {
        allowedUsers: ["all"],
        name: "init",
      },
    },
    {
      path: "unauthorized",
      loadComponent: () => import('./pages/unauthorize/unauthorize.component').then(c => c.UnauthorizeComponent),
      data: {
        name: "unauthorized",
      },
    },
    {
      path: "expired-shared-result",
      loadComponent: () => import('./pages/expired-shared-result/expired-shared-result.component').then(c => c.ExpiredSharedResultComponent),
    },
    {
      path: "WAS",
      redirectTo: "was",
    },
    {
      path: "home",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/login-redirect/login-redirect.component').then(c => c.LoginRedirectComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "create-new-patient",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/index/index.component').then(c => c.IndexComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "find-patient",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/find-patient/find-patient.component').then(c => c.FindPatientComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "search-patient",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/search-patient/search-patient.component').then(c => c.SearchPatientComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "my-test-results",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/test-result/test-result.component').then(c => c.TestResultComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "test-results-detail",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/test-result-detail/test-result-detail.component').then(c => c.TestResultDetailComponent),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: 'view-result/:mpiId/:documentId',
      loadComponent: () => import('./v2/view-result/view-result.component').then(c => c.ViewResultComponent),
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
    },
    {
      path: "results",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        PtEeGuard,
        MFAGuard,
      ],
      // canDeactivate: [deactivateResultsGuard],
      loadChildren: () => {
        const fullCatalogueViewEnabled = String(config._envConfig.FullCatalogueViewEnabled).toLocaleLowerCase() == 'true';
        const themeName = String(config._envConfig.ThemeName).toLocaleLowerCase();

        if (themeName === ThemeNames.BluePulse) {
          return import('./v2/v2.module').then(m => m.V2Module);
        } else if (fullCatalogueViewEnabled) {
          return import("./pages/results-v2/results-v2.module").then((module) => module.ResultsV2Module);
        } else {
          return import("./pages/results/results.module").then((d) => d.ResultsModule);
        }
      },
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "results/v2",
      canActivate: [AuthGuard, UserTypeGuard],
      loadChildren: () =>
        import("./pages/results-v2/results-v2.module").then(
          (module) => module.ResultsV2Module
        ),
      data: {
        allowedUsers: ["all"],
      },
    },
    {
      path: "register-and-test",
      canActivate: [
        AuthGuard,
        UserTypeGuard,
        CountryGuard,
        IsTeacherGuard,
        PtEeGuard,
        MFAGuard,
      ],
      loadComponent: () => import('./pages/register-and-test/register-and-test.component').then(c => c.RegisterAndTestComponent),
      data: {
        allowedUsers: ["school_teacher"],
      },
    },
    // {
    //   path: "individual",
    //   canActivate: [AuthGuard, UserTypeGuard, CountryGuard, MFAGuard],
    //   loadChildren: () =>
    //     import("./pages/individual-test/individual-test.module").then(
    //       (d) => d.IndividualTestModule
    //     ),
    //   data: {
    //     allowedUsers: ["all"],
    //   },
    // },
    // {
    //   path: "generate",
    //   canActivate: [AuthGuard, UserTypeGuard, CountryGuard, MFAGuard],
    //   loadChildren: () =>
    //     import("./pages/generate-order/generate-individual-order.module").then(
    //       (d) => d.GenerateIndividualOrderModule
    //     ),
    //   data: {
    //     allowedUsers: ["all"],
    //   },
    // },
    {
      path: "wardresult",
      loadChildren: () =>
        import(
          "./pages/parent-child-view-result/parent-child-view-result.module"
        ).then((d) => d.ParentChildViewResultModule),
    },
    {
      path: "wr",
      redirectTo: "wardresult",
    },
    {
      path: "createpoolorder",
      canActivate: [AuthGuard, CountryGuard, MFAGuard],
      loadChildren: () =>
        import("./pages/pool-order/pool-order.module").then(
          (m) => m.PoolOrderModule
        ),
    },
    {
      path: "individualtesting",
      canActivate: [AuthGuard, CountryGuard],
      loadChildren: () =>
        import(
          "./pages/company-individual-testing/company-individual-testing.module"
        ).then((a) => a.CompanyIndividualTestingModule),
    },
    {
      path: "not-found",
      loadComponent: () => import('./pages/not-found-page/not-found-page.component').then(c => c.NotFoundPageComponent),
    },
    {
      path: "**",
      canActivate: [NotFoundGuard],
      loadComponent: () => import('./pages/init/init.component').then(c => c.InitComponent),
      pathMatch: "full",
    },
  ];
  return routes;
}

@NgModule({
  imports: [RouterModule.forRoot([], { preloadingStrategy: PreloadAllModules })],
  providers: [
    {
      provide: ROUTES,
      useFactory: routesFactory,
      multi: true,
      deps: [ConfigService, Store],
    },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
